import { Helmet, HelmetData } from "react-helmet-async";
import { useRef } from "react";
import CheckMarkCircleIcon from "../../icons/CheckMarkCircleIcon";
import {
  AI_BG_NEW,
  AI_Platform_Context_Icon,
  AI_Platform_Enterprise_Icon,
  AI_Platform_Hero_Img,
  AI_Platform_Security_Icon,
  AI_Platform_Transparency_Icon,
  Use_Case_Bg,
  Who_AI_Platform_Img,
  Why_AI_Platform_Img,
} from "../../images";
import { NewFooter } from "../newFooter";
import useIntersectionObserver from "../../utils/useIntersectionObserver";

export default function AIPlatform() {
  const targetRef = useRef(null);

  useIntersectionObserver({ targetRef });

  const helmetData = new HelmetData({});

  const why = [
    {
      icon: AI_Platform_Security_Icon,
      title: "Security First",
      text: "Fine-tune and run AI securely without exposing sensitive data to external systems",
    },
    {
      icon: AI_Platform_Transparency_Icon,
      title: "Transparency & Explainability",
      text: "Every insight is traceable, so you can trust not only the answer but how it was derived",
    },
    {
      icon: AI_Platform_Context_Icon,
      title: "Context Awareness",
      text: "Insights are personalised to your company’s goals, KPIs, and industry context",
    },
    {
      icon: AI_Platform_Enterprise_Icon,
      title: "Enterprise Ready",
      text: "Built for CIOs/CTOs who demand reliability, compliance, and control over AI in the enterpriseBuilt for CIOs/CTOs who demand reliability, compliance, and control over AI in the enterprise",
    },
  ];
  return (
    <>
      <Helmet helmetData={helmetData}>
        <title>Secure AI Platform for Enterprise Analytics Africa | Analytics Intelligence Africa</title>
        <link rel="canonical" href="https://africa.analyticsintelligence.com/ai-platform" />
        <meta
          name="description"
          content="The [AI] Platform is a secure enterprise AI analytics platform that connects to your data sources, orchestrates AI agents, and delivers context-aware insights through natural language — built for banks, fintechs, and enterprises across Africa."
        />
        <meta
          name="keywords"
          content="Analytics Intelligence Africa, AI platform Africa, enterprise AI platform Africa, secure analytics platform, AI for banking Africa, AI for fintech Africa, RAG platform Africa, data analytics AI Africa, large language models Africa, enterprise analytics Africa, AI agents Africa, analytics AI Nigeria, analytics AI Ghana, analytics AI Kenya, predictive analytics Africa, AI infrastructure Africa"
        />
        <meta property="og:title" content="Secure AI Platform for Enterprise Analytics | Analytics Intelligence Africa" />
        <meta property="og:description" content="Connect data, orchestrate AI agents, and deliver trusted insights with the [AI] Platform — purpose-built for enterprises across Africa." />
        <meta property="og:url" content="https://africa.analyticsintelligence.com/ai-platform" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Secure AI Platform for Enterprise Analytics | Analytics Intelligence Africa" />
        <meta name="twitter:description" content="Connect data, orchestrate AI agents, and deliver trusted insights with the [AI] Platform — purpose-built for enterprises across Africa." />
        <meta name="twitter:image" content="https://africa.analyticsintelligence.com/favicon.png" />
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            "name": "[AI] Platform",
            "applicationCategory": "BusinessApplication",
            "url": "https://africa.analyticsintelligence.com/ai-platform",
            "description": "A secure enterprise AI analytics platform that connects to data sources, orchestrates AI agents, and delivers context-aware insights through natural language for enterprises across Africa.",
            "provider": {
              "@type": "Organization",
              "name": "Analytics Intelligence Africa",
              "url": "https://africa.analyticsintelligence.com"
            },
            "operatingSystem": "Cloud, On-Premises, Hybrid",
            "offers": {
              "@type": "Offer",
              "availability": "https://schema.org/InStock"
            }
          }
        `}</script>
      </Helmet>
      <main>
        <section
          style={{
            backgroundImage: `url("${AI_BG_NEW}")`,
            backgroundSize: "cover",
          }}
          className="min-h-[100vh] flex items-center bg-left-bottom md:bg-center"
          ref={targetRef}
        >
          <div className="container mx-auto w-full h-full flex flex-col md:flex-row justify-between items-center gap-16 md:gap-20 lg:gap-28 xl:gap-36  pt-[126px] pb-11 md:py-[96px] py-[96px]">
            <div className="flex basis-2/4 flex-col flex-wrap gap-[16px] items-center md:items-start">
              <div className="space-y-8 md:space-y-11 flex flex-col items-center md:items-start">
                <span className="text-sm md:text-base bg-[#EFF0F71F] px-3 p-[6px] rounded-[100px] text-white">
                  AI Platform
                </span>
                <div className="space-y-4">
                  <h2 className="text-4xl md:text-[40px] xl:text-[48px] text-center md:text-left font-extrabold leading-[1.3] md:leading-[54px] text-white">
                    The Secure AI Platform for{" "}
                    <br className="hidden xl:block" /> Analytics in the
                    Enterprise
                  </h2>
                  <p className="text-sm md:text-lg text-center md:text-left font-medium text-[#E3E3E3] leading-5">
                    Delivering context-aware annotations and model evaluations
                    that bridge global AI technology with accuracy
                  </p>
                </div>
              </div>
            </div>
            <div className="basis-2/4 flex justify-end">
              <img
                className=" w-full max-w-[680px]"
                src={AI_Platform_Hero_Img}
                alt="illustration"
              />
            </div>
          </div>
        </section>

        <section className="bg-white relative py-[96px] overflow-hidden">
          <div className="container mx-auto flex flex-col gap-11">
            <div className="h-[300px] w-[300px] xl:h-[450px] xl:w-[450px] bg-[#7AA8FF] blur-[200px] absolute top-0 -right-[70%] md:-right-[45%] lg:-right-[15%]"></div>
            <div className="flex flex-col items-center text-center md:text-left z-[100]">
              <h2 className="text-black font-extrabold text-[24px] md:text-[32px] text-center">
                What is [AI] Platform?
              </h2>
              <p className="text-black text-sm md:text-base max-w-[1086px] text-center">
                The [AI] Platform is built to transform how enterprises activate
                and govern their data for AI. It enables you to create analytics
                agents that connect to your data sources, understand intent,
                orchestrate workflows, and deliver insights through natural
                language.
              </p>
            </div>
            <div className="flex flex-col-reverse md:flex-row items-center mt-0 md:mt-11 gap-8">
              <div className="flex-1 flex items-center justify-center">
                <img
                  src={Why_AI_Platform_Img}
                  alt="Data Structures"
                  className="md:max-w-[500px] h-auto"
                />
              </div>

              <div class="flex-1 space-y-3">
                <h2 class="text-base md:text-lg xl:text-xl font-bold text-black text-center md:text-left">
                  At its core, the platform combines:
                </h2>

                <div class="space-y-2 mb-12">
                  <div class="flex items-start text-sm md:text-base gap-3">
                    <span class="text-[#000]">•</span>
                    <div>
                      <span class="font-bold text-black">
                        Enterprise Data Connectivity:
                      </span>
                      <span class="text-black ml-1">
                        Cloud and on-premises integrations across behavioural,
                        operational and market data sources
                      </span>
                    </div>
                  </div>

                  <div class="flex items-start text-sm md:text-base gap-3">
                    <span class="text-black">•</span>
                    <div>
                      <span class="font-bold text-black">
                        Data Language Model (DLM):
                      </span>
                      <span class="text-black ml-1">
                        Our proprietary small language model framework optimised
                        for analytics workloads
                      </span>
                    </div>
                  </div>

                  <div class="flex items-start text-sm md:text-base gap-3">
                    <span class="text-black">•</span>
                    <div>
                      <span class="font-bold text-black">
                        Analytics Engine:
                      </span>
                      <span class="text-black ml-1">
                        Executes analytical calculations securely, consistently,
                        and transparently.
                      </span>
                    </div>
                  </div>

                  <div class="flex items-start text-sm md:text-base gap-3">
                    <span class="text-black">•</span>
                    <div>
                      <span class="font-bold text-black">
                        Agent Skills & Tools:
                      </span>
                      <span class="text-black ml-1">
                        Extend agent capabilities with industry, role, and
                        domain-specific logic.
                      </span>
                    </div>
                  </div>

                  <div class="flex items-start text-sm md:text-base gap-3">
                    <span class="text-black">•</span>
                    <div>
                      <span class="font-bold text-black">
                        Multi-Channel Delivery:
                      </span>
                      <span class="text-black ml-1">
                        Insights delivered via chat, email, Slack, Teams,
                        WhatsApp and more
                      </span>
                    </div>
                  </div>

                  <div class="flex items-start text-sm md:text-base gap-3">
                    <span class="text-black">•</span>
                    <div>
                      <span class="font-bold text-black">
                        Secure Fine-Tuning:
                      </span>
                      <span class="text-black ml-1">
                        Adapt models within your private environment without
                        data leaving controlled infrastructure
                      </span>
                    </div>
                  </div>
                </div>

                <div class="text-sm md:text-base">
                  <p class="text-black leading-relaxed">
                    The platform is powered by our MCP Server, enabling data
                    unification and contextual enrichment so that LLMs can
                    reason using structured, unstructured, and domain metadata
                    securely.
                  </p>
                </div>
                <div class="text-sm md:text-base">
                  <h3 class="font-extrabold text-black">The result:</h3>
                  <p class="text-black leading-relaxed">
                    A platform where every stakeholder, from analyst to
                    executive, can get fast, trusted, context-aware insights.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section
          className="bg-[#EDF1FF] py-[48px] bg-left-bottom md:bg-center bg-cover md:bg-cover"
          style={{
            backgroundImage: `url("${Use_Case_Bg}")`,
          }}
        >
          <div className="container space-y-12">
            <div className="mb-10 text-center">
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight xl:text-[36px] text-[#FFF] mb-3">
                Why Client's Choose Us
              </h1>
              <p className="mx-auto max-w-5xl font-normal text-base text-white">
                Most analytics and AI platforms provide generic results that
                lack context and transparency. We built the [AI] Platform
                differently.
              </p>
            </div>

            <div className="grid grid-cols-12 gap-y-8 sm:gap-8">
              {why.map((item, idx) => (
                <div
                  key={idx}
                  className="col-span-12 sm:col-span-6 xl:col-span-3 bg-white flex flex-col items-center rounded-xl px-3 py-6 gap-4"
                >
                  <img
                    src={item.icon}
                    alt={`${item.title} illustration`}
                    className="w-16 h-16"
                  />
                  <div className="space-y-2 text-center">
                    <h5 className="text-lg md:text-xl font-bold text-[#020128]">
                      {item.title}
                    </h5>
                    <p className="text-sm md:text-base text-[#020128]">
                      {item.text}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white relative py-[96px]">
          <div className="container flex flex-col gap-11">
            <div className="flex flex-col items-center text-center md:text-left z-[100]">
              <h2 className="text-black font-extrabold text-[24px] md:text-[32px] text-center">
                Who is it for?
              </h2>
              <p className="text-black text-sm md:text-base max-w-[1086px] text-center">
                The [AI] Platform is designed for multiple stakeholders across
                the enterprise:
              </p>
            </div>
            <div className="flex flex-col-reverse md:flex-row items-center mt-0 md:mt-11 gap-8">
              <div className="flex-1 flex items-center justify-center">
                <img
                  src={Who_AI_Platform_Img}
                  alt="Data Structures"
                  className="md:max-w-[500px] h-auto"
                />
              </div>

              <div className="flex-1 flex flex-col gap-6">
                <div className="flex items-center gap-3 md:gap-2 bg-[#EFF0F71F] rounded-[100px] py-2 px-3 w-full md:w-fit">
                  <div className="">
                    <CheckMarkCircleIcon />
                  </div>
                  <div>
                    <p className="text-black text-base">
                      <span className="font-extrabold">CIOs & CTOs:</span> Who
                      need a secure AI infrastructure to protect proprietary
                      enterprise data
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 md:gap-2 bg-[#EFF0F71F] rounded-[100px] py-2 px-3 w-fit">
                  <div className="">
                    <CheckMarkCircleIcon />
                  </div>
                  <p className="text-black text-base">
                    <span className="font-extrabold">
                      Heads of Analytics & Data Science:
                    </span>{" "}
                    who want to scale analytic reasoning and automate workflows
                    reliably
                  </p>
                </div>
                <div className="flex items-center gap-3 md:gap-2 bg-[#EFF0F71F] rounded-[100px] py-2 px-3 w-fit">
                  <div className="">
                    <CheckMarkCircleIcon />
                  </div>
                  <p className="text-black text-base">
                    <span className="font-extrabold">
                      Business Decision Makers:
                    </span>{" "}
                    who require fast, contextual, data-driven intelligence to
                    drive performance
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        <NewFooter />
      </main>
    </>
  );
}
